<template>
  <DialogHeader>Registration details</DialogHeader>
  <DialogContent>
    <div class="border border-[#EEEDEC] rounded-xl overflow-hidden bg-white">
      <!-- Header Row -->
      <div
        class="flex px-[14px] py-[7px] bg-gray-200/15 font-secondary text-[12px] leading-[15px] text-[#656666] font-medium uppercase"
      >
        <div class="flex-1 min-w-[200px]">Description</div>
        <div>Amount</div>
      </div>

      <div class="px-[14px] pt-2.5 pb-5">
        <template v-if="props.estimatedCost">
          <div
            v-for="cost in props.estimatedCost.breakdown"
            :key="cost.origin.id"
            class="w-full font-secondary text-[13px] leading-[10xp] text-gray-800 mb-3 last:mb-0"
          >
            <div class="font-medium mb-1.5">{{ cost.description }}</div>
            <div class="flex flex-col pl-2.5">
              <div
                v-for="item in cost.breakdown"
                :key="item.id"
                class="flex items-center justify-between"
              >
                <div>{{ item.name }}</div>
                <div>
                  {{ formatPrice({ amount: item.total.amount }) }}
                </div>
              </div>
              <div class="flex items-center justify-between">
                <div class="font-medium">Subtotal</div>
                <div class="font-medium text-[15px] leading-[17px]">
                  {{ formatPrice({ amount: cost.total.amount }) }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div
            v-for="origin in props.selectedOrigins"
            :key="origin.id"
            class="w-full font-secondary text-[13px] leading-[10xp] text-gray-800 mb-3 last:mb-0"
          >
            <div class="font-medium mb-1.5">{{ origin.name }}</div>
            <div class="flex flex-col pl-2.5">
              <div
                v-for="item in origin.registrationCost?.breakdown ?? []"
                :key="item.id"
                class="flex items-center justify-between"
              >
                <div>{{ item.name }}</div>
                <div>
                  {{
                    formatPrice({
                      amount: item.total.amount,
                    })
                  }}
                </div>
              </div>
              <div class="flex items-center justify-between">
                <div class="font-medium">Subtotal</div>
                <div class="font-medium text-[15px] leading-[17px]">
                  {{
                    formatPrice({
                      amount: origin.registrationCost?.total.amount ?? 0,
                    })
                  }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <div
          v-if="!props.estimatedCost && !props.selectedOrigins.length"
          class="w-full font-secondary text-[13px] text-gray-500 text-center py-2"
        >
          No origins selected
        </div>
      </div>

      <div class="border-t border-[#EEEDEC] h-10 flex items-center px-[14px]">
        <div
          class="flex-1 pr-[14px] font-primary font-medium text-[#656666] text-[12px] leading-[21px] uppercase text-right tracking-[0.5px]"
        >
          Total
        </div>
        <div
          class="h-full flex items-center justify-end font-secondary font-medium text-[18px] leading-[18px] text-gray-800 border-l border-[#EEEDEC] w-[116px]"
        >
          {{ total }}
        </div>
      </div>
    </div>
  </DialogContent>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import type {
  OriginsQuery,
  EstimateRegistrationCostQuery,
} from '@/shared/graphql/router/graphql'
import { formatPrice } from '@/shared/utils/money'

const props = defineProps<{
  selectedOrigins: OriginsQuery['origins']
  estimatedCost?: EstimateRegistrationCostQuery['estimateRegistrationCost']
}>()

const total = computed(() => {
  if (props.estimatedCost) {
    return formatPrice({ amount: props.estimatedCost.total.amount })
  }
  const sum = props.selectedOrigins.reduce((acc, origin) => {
    return acc + Number(origin.registrationCost?.total.amount ?? 0)
  }, 0)
  return formatPrice({ amount: sum })
})
</script>
